// CurvaDeJuros.js
import React, { useState, useEffect, useMemo } from 'react';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, ReferenceLine 
} from 'recharts';

const API_BASE = process.env.REACT_APP_API_URL || '/api/go';

// Full list of DI1 Futures contracts provided
const DI_TICKERS = [
  'DI1F27', 'DI1F28', 'DI1F29', 'DI1F30', 'DI1F31', 'DI1F33', 'DI1F32', 'DI1F35',
  'DI1N27', 'DI1J26', 'DI1V26', 'DI1J27', 'DI1N28', 'DI1N29', 'DI1F37', 'DI1N26',
  'DI1V27', 'DI1N30', 'DI1V28', 'DI1J28', 'DI1J29', 'DI1Z26', 'DI1F34', 'DI1G27',
  'DI1V29', 'DI1F36', 'DI1J31', 'DI1M26', 'DI1J30', 'DI1K26', 'DI1U26', 'DI1Q26',
  'DI1H26', 'DI1X26', 'DI1V30', 'DI1Q27', 'DI1V31', 'DI1N31', 'DI1F38', 'DI1F41',
  'DI1F40', 'DI1F39'
];

// Helper: Map B3 Month Codes to readable format
const MONTH_CODES = {
  'F': { month: 0, label: 'Jan' },
  'G': { month: 1, label: 'Fev' },
  'H': { month: 2, label: 'Mar' },
  'J': { month: 3, label: 'Abr' },
  'K': { month: 4, label: 'Mai' },
  'M': { month: 5, label: 'Jun' },
  'N': { month: 6, label: 'Jul' },
  'Q': { month: 7, label: 'Ago' },
  'U': { month: 8, label: 'Set' },
  'V': { month: 9, label: 'Out' },
  'X': { month: 10, label: 'Nov' },
  'Z': { month: 11, label: 'Dez' }
};

export const CurvaDeJuros = ({ theme }) => {
  const [curveData, setCurveData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ short: 0, long: 0, slope: 0 });

  useEffect(() => {
    fetchCurveData();
  }, []);

  const parseTickerDate = (ticker) => {
    // Format: DI1 + MonthCode + Year (2 digits)
    // Ex: DI1F27 -> F (Jan), 27 (2027)
    try {
      const code = ticker.substring(3, 4); // The letter
      const yearShort = ticker.substring(4); // The numbers
      const year = 2000 + parseInt(yearShort, 10);
      
      const monthData = MONTH_CODES[code];
      if (!monthData) return null;

      const dateObj = new Date(year, monthData.month, 1);
      
      return {
        dateObj,
        label: `${monthData.label}/${yearShort}`, // Ex: Jan/27
        sortKey: dateObj.getTime()
      };
    } catch (e) {
      return null;
    }
  };

  const fetchCurveData = async () => {
    try {
      setLoading(true);
      
      const promises = DI_TICKERS.map(async (ticker) => {
        try {
          const res = await fetch(`${API_BASE}/api/ticker/${ticker}`);
          if (!res.ok) return null;
          const data = await res.json();
          
          const meta = parseTickerDate(ticker);
          if (!meta) return null;

          // For DI1 futures, 'ult' (last) or 'fec' (close) usually represents the RATE (Taxa)
          const rate = data.ult || data.fec || 0;

          // Filter out stale or zero data which breaks the curve visualization
          if (rate <= 0) return null; 

          return {
            ticker,
            rate: rate,
            date: meta.dateObj,
            label: meta.label,
            sortKey: meta.sortKey,
            change: data.var || 0
          };
        } catch (err) {
          return null;
        }
      });

      const results = await Promise.all(promises);
      
      // Filter nulls and Sort by Date (Shortest to Longest maturity)
      const cleanData = results
        .filter(r => r !== null)
        .sort((a, b) => a.sortKey - b.sortKey);

      setCurveData(cleanData);

      // Calculate Stats (Steepness/Slope)
      if (cleanData.length > 1) {
        const shortTerm = cleanData[0].rate;
        const longTerm = cleanData[cleanData.length - 1].rate;
        setStats({
          short: shortTerm,
          long: longTerm,
          slope: (longTerm - shortTerm) * 100 // Basis Points
        });
      }

    } catch (err) {
      console.error("Error fetching DI Curve", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        background: theme.cardBg,
        borderRadius: '16px',
        padding: '1.5rem',
        border: `1px solid ${theme.border}`,
        minHeight: '350px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.textSecondary
      }}>
        Construindo curva de juros...
      </div>
    );
  }

  return (
    <div style={{
      background: theme.cardBg,
      borderRadius: '16px',
      padding: '1.5rem',
      border: `1px solid ${theme.border}`,
      backdropFilter: 'blur(10px)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
        <div>
           <div style={{
            fontSize: '0.75rem', color: theme.textSecondary, textTransform: 'uppercase',
            fontWeight: 'bold', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem'
          }}>
            📊 Futuros de DI1 (B3)
            <span style={{ background: 'rgba(59, 130, 246, 0.2)', color: '#3b82f6', padding: '2px 8px', borderRadius: '4px', fontSize: '0.65rem' }}>
              YIELD CURVE
            </span>
          </div>
          <h3 style={{ fontSize: '1.2rem', fontWeight: 'bold', color: theme.textPrimary, margin: 0 }}>
            Curva de Juros
          </h3>
          <p style={{ fontSize: '0.85rem', color: theme.textSecondary, margin: '0.25rem 0 0 0' }}>
            Estrutura a termo da taxa de juros (ETTJ)
          </p>
        </div>

        {/* Stats Box - Inclinação */}
        <div style={{ textAlign: 'right' }}>
           <div style={{ fontSize: '0.7rem', color: theme.textSecondary }}>Inclinação (Slope)</div>
           <div style={{ 
             fontSize: '1.2rem', 
             fontWeight: 'bold', 
             color: stats.slope >= 0 ? '#22c55e' : '#ef4444',
             fontFamily: 'monospace' 
           }}>
             {stats.slope > 0 ? '+' : ''}{stats.slope.toFixed(0)} bps
           </div>
        </div>
      </div>

      {/* The Curve Chart */}
      <div style={{ height: '300px', width: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={curveData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="colorRate" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
            
            <XAxis 
              dataKey="label" 
              stroke={theme.textSecondary} 
              fontSize={10} 
              tickMargin={10}
              interval="preserveStartEnd"
              minTickGap={30}
            />
            
            <YAxis 
              domain={['auto', 'auto']} 
              stroke={theme.textSecondary} 
              fontSize={10}
              tickFormatter={(val) => `${val.toFixed(1)}%`}
            />
            
            <Tooltip
              contentStyle={{ 
                background: theme.cardBg, 
                border: `1px solid ${theme.border}`, 
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.5)'
              }}
              labelStyle={{ color: theme.textSecondary, marginBottom: '0.5rem' }}
              itemStyle={{ color: theme.textPrimary, fontWeight: 'bold' }}
              formatter={(value) => [`${value.toFixed(2)}%`, 'Taxa DI']}
            />

            <Area 
              type="monotone" 
              dataKey="rate" 
              stroke="#3b82f6" 
              strokeWidth={3}
              fillOpacity={1} 
              fill="url(#colorRate)" 
              activeDot={{ r: 6, strokeWidth: 0, fill: '#fff' }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Footer Info / Key Rates */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem',
        paddingTop: '1rem', marginTop: '1rem', borderTop: `1px solid ${theme.border}`
      }}>
        {/* Short End */}
        <div style={{ background: 'rgba(59, 130, 246, 0.05)', borderRadius: '8px', padding: '0.75rem' }}>
          <div style={{ fontSize: '0.7rem', color: theme.textSecondary, marginBottom: '0.25rem' }}>
            Curto ({curveData[0]?.label})
          </div>
          <div style={{ fontSize: '1rem', fontWeight: 'bold', color: theme.textPrimary }}>
            {stats.short.toFixed(2)}%
          </div>
        </div>

        {/* Mid Curve (approx middle of array) */}
        <div style={{ background: 'rgba(59, 130, 246, 0.05)', borderRadius: '8px', padding: '0.75rem' }}>
          <div style={{ fontSize: '0.7rem', color: theme.textSecondary, marginBottom: '0.25rem' }}>
            Médio ({curveData[Math.floor(curveData.length / 2)]?.label})
          </div>
          <div style={{ fontSize: '1rem', fontWeight: 'bold', color: theme.textPrimary }}>
            {curveData[Math.floor(curveData.length / 2)]?.rate.toFixed(2)}%
          </div>
        </div>

        {/* Long End */}
        <div style={{ background: 'rgba(59, 130, 246, 0.05)', borderRadius: '8px', padding: '0.75rem' }}>
          <div style={{ fontSize: '0.7rem', color: theme.textSecondary, marginBottom: '0.25rem' }}>
            Longo ({curveData[curveData.length - 1]?.label})
          </div>
          <div style={{ fontSize: '1rem', fontWeight: 'bold', color: theme.textPrimary }}>
            {stats.long.toFixed(2)}%
          </div>
        </div>
      </div>
    </div>
  );
};

export default CurvaDeJuros;